import { ChangeEvent, FC, useEffect, useState } from 'react';
import { MAX_PAGE_LIMIT } from '../../constants/numbers.ts';
import IResponse, { IPagination } from '../../interfaces/response.ts';
import { useTranslation } from 'react-i18next';
import { filterList } from '../../utils/array.ts';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { Box } from '@mui/material';
import BaranDataGrid from '../common/BaranDataGrid.tsx';
import { Delete } from '@mui/icons-material';
import PriceModal from './PriceModal.tsx';

type IAdPagesTable = {
	id?: number;
	selectedSocialMedia?: number;
};

interface PLPPage {
	id: number;
	title: string;
	type: string;
	description: string;
	final_price: number;
	action?: string;
}

const PriceList: FC<IAdPagesTable> = ({ id, selectedSocialMedia }) => {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<PLPPage[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [openPriceModal, setOpenPriceModal] = useState(true);
	const [editPrice, setEditPrice] = useState<number>(0);
	const [openAlert, setOpenAlert] = useState<boolean>(true);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleEditPage = (id: number) => {
		setOpenPriceModal(true);
		setEditPrice(id);
	};
	const handleCreateNewPackage = () => {
		setOpenAlert(true);
		setOpenPriceModal(true);
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};
	const handleClosePriceModal = () => {
		setOpenPriceModal(false);
		setEditPrice(0);
	};
	const handleDelete = (id: number) => {
		axios
			.delete(`media_prices/${id}`)
			.then(res => {
				if (res.data.success) {
					setFetchedData(prevData => prevData.filter(item => item.id !== id));
				}
			})
			.catch(error => {
				console.error('Error deleting data:', error);
			});
	};

	const properties: (keyof PLPPage)[] = ['title', 'type', 'description', 'final_price', 'action'];
	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<PLPPage>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'type',
			headerName: t('content_type'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return t(params.row.type);
			},
		},
		{
			field: 'description',
			headerName: t('description'),
			flex: 1,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'price',
			headerName: t('regular_price'),
			width: 250,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'campaign_type',
			headerName: t('campaign_of_type'),
			width: 250,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'co_price',
			headerName: t('collaboration_price'),
			width: 250,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 150,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				const id_ = params.id;
				return (
					<>
						<IconButton aria-label="edit" onClick={() => handleEditPage(Number(id_))}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton aria-label="delete" onClick={() => handleDelete(Number(id_))}>
							<Delete className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		setLoading(true);
		let searchObject = '';
		if (searchTerm) {
			searchObject = JSON.stringify([
				{
					field: 'title',
					value: searchTerm ?? '',
				},
				{
					field: 'price',
					value: searchTerm ?? '',
				},
				{
					field: 'co_price',
					value: searchTerm ?? '',
				},
			]);
		}
		axios<IResponse<PLPPage[]>>({
			url: `media_prices`,
			params: {
				page: page,
				limit: pageSize,
				search: searchObject,
				media_entity_id: id,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.catch(error => {
				console.error('Error fetching data:', error);
				setFetchedData([]);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, searchTerm, id, openPriceModal]);

	return (
		<>
			<Box className={`mb-14 mt-5 w-full`}>
				<BaranDataGrid<PLPPage> noDataText="for_go_to_next_step_you_should_click_on_button" loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} searchableFields={properties} actionButtonLable={t('add_tariff')} onClick={handleCreateNewPackage} />
			</Box>
			<PriceModal setOpenAlert={setOpenAlert} openAlert={openAlert} open={openPriceModal} onClose={handleClosePriceModal} id={Number(id ?? '0')} media_id={selectedSocialMedia ?? 0} editPrice={editPrice} />
		</>
	);
};

export default PriceList;
